/**
 * EAA-1839: shared types for the org-scoped accounts browser
 * (`/console/hierarchy/accounts`, design frames 34/35).
 *
 * BACKEND-CONTRACT NOTES that shape every type here — read before changing them,
 * because each one is a real limitation of `GET /organizations/{id}/accounts` and
 * the two bulk endpoints rather than a UI choice:
 *
 * 1. NO SERVER SEARCH. The endpoint takes no `search` param at all, and the row
 *    shape has no name/username to search anyway (`AssignmentSerializer` →
 *    `user_sub` + `user_email` only). So there is no `search` member in
 *    `ScopedAccountsFilters` — the search term is NOT a server filter and must
 *    never be sent as one.
 *
 *    This used to end "the frame's search box renders disabled", and that stopped
 *    being true: the box works now, as a capped CLIENT-side filter over a page
 *    walk (`useScopedAccounts.searchWalkQuery`). The endpoint fact above is
 *    unchanged — what changed is the conclusion drawn from it.
 * 2. NO FACET-COUNT ENDPOINT. Every count in the left sidebar is its own
 *    `page_size=1` request reading `.count` — see `useScopedAccounts`. Hence
 *    `FacetOption.count` is `number | null` ("still loading / not asked"), never
 *    `0` as a stand-in for unknown.
 * 3. `never_logged_in` DOES NOT EXIST as an issue facet and is not silently
 *    ignored either — it is a `VALIDATION_ERROR`. `FacetOption.disabledReason`
 *    exists so that row can be shown, greyed, with the true reason.
 * 4. ORDERING is limited to `granted_at` / `user_sub` / `sync_status`
 *    (`OrganizationAccountsView.ALLOWED_ORDER_FIELDS`). `ScopedAccountSortField`
 *    is exactly that set — no "last login" sort is possible.
 */

import type { ScopedAccountIssue, ScopedAccountRow } from '@/api/orgAdmin';

/** Exactly `OrganizationAccountsView.ALLOWED_ORDER_FIELDS`. */
export type ScopedAccountSortField = 'granted_at' | 'user_sub' | 'sync_status';

export type SortDirection = 'asc' | 'desc';

/**
 * Every server-side filter this screen drives. All single-select, matching the
 * endpoint: each param is one exact-match value, not a list.
 */
export interface ScopedAccountsFilters {
    includeChildren: boolean;
    issue: ScopedAccountIssue | null;
    levelId: string | null;
    /** Only meaningful while `includeChildren` is true — see `types.ts` note in the API client. */
    childOrgId: string | null;
    isActive: boolean | null;
}

export const EMPTY_FILTERS: ScopedAccountsFilters = {
    // EAA-1839 (2026-08-21, user decision): FIXED `true`, permanently, with no UI
    // control. Frame 35 draws a "Phạm vi" toggle over this value
    // ("Gồm toàn bộ nhánh con" / "Chỉ tài khoản trực thuộc"); it was built twice
    // and removed both times because the rail's "Đơn vị" facet group already
    // expresses the narrower state — its first option IS the scope org alone
    // (`directOrgLabel`, `child_organization_id` = scope org id), which replaces
    // rather than widens the effective scope. Do not read the gate on this value
    // in `useScopedAccountFacets` (`if (filters.includeChildren && orgId)`) as
    // dead code to delete — it is a real precondition (`orgId` still varies),
    // it is just no longer paired with a control that can turn it off.
    includeChildren: true,
    issue: null,
    levelId: null,
    childOrgId: null,
    /*
     * EAA-1817 (2026-08-25, user report: "it error create new rows but account is
     * show id"). This was `null` — no `is_active` filter at all — so the list
     * showed REVOKED assignments alongside live ones. That is not a cosmetic
     * choice: a revoked grant is history, and every action on this screen (assign
     * Level, change unit, reset password, suspend) operates on current membership.
     *
     * What it cost, in the reporter's own data: seven rows left over from an old
     * `email-backfill` pass had nothing but revoked assignments, and their
     * `user_sub` values resolve to no realm account — so they rendered as
     * truncated UUIDs with no email. "Chọn tất cả" swept them up with everyone
     * else and the bulk Level-assign granted all seven a live Level, creating
     * seven active grants for people who do not exist.
     *
     * `true` makes the list mean what its title says. The "Tình trạng" facet still
     * offers the revoked side explicitly, so the history stays one click away
     * rather than being deleted from the UI.
     */
    isActive: true,
};

/** Which sidebar group an option belongs to — drives the "count everything except my own group" rule. */
export type FacetGroupKey = 'issue' | 'level' | 'childOrg' | 'status';

/**
 * EAA-1820: `FacetGroup`/`FacetOption` moved to `accounts/shared/facetTypes.ts`
 * so the realm screen's rail could finally use the SAME shape (see that file's
 * docstring) — re-exported here so every existing import in this folder
 * (`hierarchy/scopedAccounts/*`) keeps working unchanged.
 */
export type { FacetGroup, FacetOption } from '@/pages/Console/Accounts/Shared/facetTypes';

/**
 * The row-status vocabulary this screen can actually derive, in priority order.
 *
 * Frame 34 draws "Bình thường / Chưa vào / Đã ngưng". "Chưa vào" is a login state
 * and has no data source (see the API client's `ScopedAccountRow` note), so it is
 * NOT rendered. What replaces it is real and already this app's established
 * vocabulary on `/console/hierarchy`: `is_active` plus the provisioning
 * `sync_status` the row genuinely carries.
 */
export type ScopedAccountStatus = 'revoked' | 'sync-error' | 'pending' | 'ok';

/**
 * EAA-1839 (2026-08-24, user request "can make it is one rows"): the board lists
 * ASSIGNMENTS, and one person legitimately holds several — `Assignment` has no
 * uniqueness constraint on `(user_sub, organization)`, deliberately, because
 * "separate account sync per app" is the whole point of the model (see its own
 * docstring, and `UserSyncService._create_or_update_assignment`'s rule 3). With
 * a parent org and its children in scope, the same account therefore appeared on
 * several rows at once, which reads as duplicate accounts.
 *
 * A group collapses every assignment of one `user_sub` into ONE row. The row
 * still IS an assignment — the most recently granted one — so every existing
 * column, action and selection key keeps working unchanged; `group` is the extra
 * context the Đơn vị / Level / Tình trạng cells render when it is present.
 *
 * `group` is ABSENT for an account holding exactly one assignment in scope, so
 * the common case carries no extra object and reads exactly as before.
 */
export interface ScopedAccountGroup {
    /** Every assignment this row stands for, most recent grant first. Length >= 2
     *  whenever `group` is present at all. */
    assignments: ScopedAccountRow[];
    /** Distinct `organization_id`s across `assignments`, in the same order. */
    orgIds: string[];
}

/**
 * A board row: an assignment, plus the group it represents when it stands for
 * more than one. Assignable to `ScopedAccountRow` on purpose — every consumer
 * that does not care about grouping needs no change.
 */
export type ScopedAccountGroupedRow = ScopedAccountRow & {
    group?: ScopedAccountGroup;
};

/**
 * Collapse assignment rows to one row per `user_sub`, preserving the incoming
 * order of first appearance so a server `ordering` still governs the board.
 *
 * The retained row is the most recently GRANTED assignment of the group, not
 * simply the first one seen: the row's own Level/Đơn vị/status cells describe a
 * specific assignment, and the newest grant is the one an operator just made and
 * expects to see. `granted_at` is an ISO-8601 string from the API, so a lexical
 * compare is a chronological compare.
 */
export function groupScopedAccountRows(
    rows: readonly ScopedAccountRow[],
    /**
     * EAA-1817: the đơn vị currently in scope. When an account holds grants in
     * several units, the one matching this becomes the row's `primary` — see below.
     */
    scopeOrgId?: string | null
): ScopedAccountGroupedRow[] {
    const bySub = new Map<string, ScopedAccountRow[]>();
    for (const row of rows) {
        const bucket = bySub.get(row.user_sub);
        if (bucket) {
            bucket.push(row);
            continue;
        }
        bySub.set(row.user_sub, [row]);
    }

    const grouped: ScopedAccountGroupedRow[] = [];
    for (const bucket of bySub.values()) {
        const assignments = [...bucket].sort((a, b) => b.granted_at.localeCompare(a.granted_at));
        /*
         * EAA-1817 (2026-08-25, user report: "on view parent can not assign level to
         * gvanhdo, only for EMG school").
         *
         * `primary` is what every row action reads — `row.organization_id` decides
         * which đơn vị an assign, a transfer or a revoke writes into. Picking it by
         * newest-granted_at made that choice arbitrary with respect to what the
         * operator is looking at: standing in EMG, a row spanning EMG and EMG school
         * acted on EMG school because that grant happened to be newer, so the EMG
         * grant could not be touched from anywhere and stayed "Chưa gán vai trò".
         *
         * The đơn vị you are standing in is the one you mean. Newest still breaks the
         * tie when the scope org holds no grant for this account (every row under
         * `include_children`), which is the original behaviour.
         */
        const inScope = scopeOrgId
            ? assignments.find((a) => a.organization_id === scopeOrgId)
            : undefined;
        const primary = inScope ?? assignments[0];
        if (assignments.length === 1) {
            grouped.push(primary);
            continue;
        }
        const orgIds: string[] = [];
        for (const a of assignments) {
            if (!orgIds.includes(a.organization_id)) orgIds.push(a.organization_id);
        }
        grouped.push({ ...primary, group: { assignments, orgIds } });
    }
    return grouped;
}
